import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSWRConfig } from 'swr'

import { useToastContext } from '../../../../contexts'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import { useCurrentTenantConfig } from '../../../../utils/tenants'
import {
  DeliveryForecastProcessCompletions,
  EInvoicePartnerType,
  EditSupplierFormDto,
  OrderProcessCompletions,
  SupplierDetail,
  useSupplierById,
} from '../data'
import {
  requestActivateSupplier,
  requestDeactivateSupplier,
  requestUpdateSupplier,
} from '../data/api'
import { SupplierForm } from './Supplier.form'

export type SupplierFormFieldName = keyof EditSupplierFormDto

type Props = {
  id: string
}

export const SupplierData: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { tenantId } = useCurrentTenantConfig()
  const { mutate: globalMutate } = useSWRConfig()
  const { data: supplier, isLoading, mutate } = useSupplierById(id)

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const supplierDetailToFormValues = (
    detail: SupplierDetail | undefined,
  ): EditSupplierFormDto => {
    return {
      hasChangesPending: detail?.HasChangesPending || false,
      id: detail?.Id || '',
      name: detail?.Name || '',
      description: detail?.Description || '',
      status: detail?.Status || 'inactive',
      inboundValue: detail?.EmailSettings.Inbound?.Value || '',
      outboundValue: detail?.EmailSettings.Outbound?.Value || '',
      supportValue: detail?.EmailSettings.Support?.Value || '',
      deliveryForecastIsProcessActiveValue:
        detail?.WebEdiSettings.DeliveryProcessForecastSettings.IsProcessActive
          .Value || false,
      deliveryForecastIsProcessActiveOverride:
        detail?.WebEdiSettings.DeliveryProcessForecastSettings.IsProcessActive
          .OverrideDefaultValue || false,
      deliveryForecastProcessCompletionsValue:
        (detail?.WebEdiSettings.DeliveryProcessForecastSettings.ProcessCompletions.Value.toLowerCase() as DeliveryForecastProcessCompletions) ||
        'confirmation',
      deliveryForecastProcessCompletionsOverride:
        detail?.WebEdiSettings.DeliveryProcessForecastSettings
          .ProcessCompletions.OverrideDefaultValue || false,
      deliveryForecastResponsePeriodValue:
        detail?.WebEdiSettings.DeliveryProcessForecastSettings.ResponsePeriod
          .Value || 0,
      deliveryForecastResponsePeriodOverride:
        detail?.WebEdiSettings.DeliveryProcessForecastSettings.ResponsePeriod
          .OverrideDefaultValue || false,
      emailLanguageValue: detail?.WebEdiSettings.EmailLanguage.Value || 'en',
      emailLanguageOverride:
        detail?.WebEdiSettings.EmailLanguage.OverrideDefaultValue || false,
      orderDeliveryDateDeviationValue:
        detail?.WebEdiSettings.OrderProcessSettings.DeliveryDateDeviation
          ?.Value || null,
      orderDeliveryDateDeviationOverride:
        detail?.WebEdiSettings.OrderProcessSettings.DeliveryDateDeviation
          ?.OverrideDefaultValue || false,
      orderIsProcessActiveValue:
        detail?.WebEdiSettings.OrderProcessSettings.IsProcessActive.Value ||
        false,
      orderIsProcessActiveOverride:
        detail?.WebEdiSettings.OrderProcessSettings.IsProcessActive
          .OverrideDefaultValue || false,
      orderProcessCompletionsValue:
        (detail?.WebEdiSettings.OrderProcessSettings.ProcessCompletions?.Value.toLowerCase() as OrderProcessCompletions) ||
        'invoice',
      orderProcessCompletionsOverride:
        detail?.WebEdiSettings.OrderProcessSettings.ProcessCompletions
          ?.OverrideDefaultValue || false,
      orderResponsePeriodValue:
        detail?.WebEdiSettings.OrderProcessSettings.ResponsePeriod?.Value || 0,
      orderResponsePeriodOverride:
        detail?.WebEdiSettings.OrderProcessSettings.ResponsePeriod
          ?.OverrideDefaultValue || false,
      orderInvoiceFormatValue:
        detail?.WebEdiSettings.OrderProcessSettings.InvoiceFormat?.Value ||
        'sap_idoc',
      orderInvoiceFormatOverride:
        detail?.WebEdiSettings.OrderProcessSettings.InvoiceFormat
          ?.OverrideDefaultValue || false,
      orderAllowPreliminaryConfirmationValue:
        detail?.WebEdiSettings.OrderProcessSettings.AllowPreliminaryConfirmation
          .Value || false,
      orderAllowPreliminaryConfirmationOverride:
        detail?.WebEdiSettings.OrderProcessSettings.AllowPreliminaryConfirmation
          ?.OverrideDefaultValue || false,
      eInvoiceLanguageValue:
        detail?.WebEdiSettings.EInvoiceLanguage.Value || 'en',
      eInvoiceLanguageOverride:
        detail?.WebEdiSettings.EInvoiceLanguage.OverrideDefaultValue || false,
      eInvoicePartnerTypeValue:
        detail?.WebEdiSettings.EInvoiceProcessSettings.EInvoicePartnerType
          .Value ?? EInvoicePartnerType.LogicalSystem,
      eInvoicePartnerTypeOverride:
        detail?.WebEdiSettings.EInvoiceProcessSettings.EInvoicePartnerType
          .OverrideDefaultValue || false,
      eInvoiceIsProcessActiveValue:
        detail?.WebEdiSettings.EInvoiceProcessSettings.IsProcessActive.Value ||
        false,
      eInvoiceIsProcessActiveOverride:
        detail?.WebEdiSettings.EInvoiceProcessSettings.IsProcessActive
          .OverrideDefaultValue || false,
      isEInvoiceSupplierEnabledValue:
        detail?.WebEdiSettings.EInvoiceProcessSettings.IsEInvoiceSupplierEnabled
          .Value || false,
      isEInvoiceSupplierEnabledOverride:
        detail?.WebEdiSettings.EInvoiceProcessSettings.IsEInvoiceSupplierEnabled
          .OverrideDefaultValue || false,
      eInvoiceAttachmentDocumentTypeValue:
        detail?.WebEdiSettings.EInvoiceProcessSettings
          .EInvoiceAttachmentDocumentType.Value || 0,
      eInvoiceAttachmentDocumentTypeOverride:
        detail?.WebEdiSettings.EInvoiceProcessSettings
          .EInvoiceAttachmentDocumentType.OverrideDefaultValue || false,
    }
  }

  const formValuesToSupplierDetails = (
    formData: EditSupplierFormDto,
  ): SupplierDetail => {
    return {
      Id: formData.id,
      Name: formData.name,
      Description: formData.description,
      Status: formData.status,
      EmailSettings: {
        Inbound: {
          Value: formData.inboundValue,
          OverrideDefaultValue: true,
        },
        Outbound: {
          Value: formData.outboundValue,
          OverrideDefaultValue: true,
        },
        Support: {
          Value: formData.supportValue,
          OverrideDefaultValue: true,
        },
      },
      WebEdiSettings: {
        DeliveryProcessForecastSettings: {
          IsProcessActive: {
            Value: formData.deliveryForecastIsProcessActiveValue,
            OverrideDefaultValue:
              formData.deliveryForecastIsProcessActiveOverride,
          },
          ProcessCompletions: {
            Value: formData.deliveryForecastProcessCompletionsValue,
            OverrideDefaultValue:
              formData.deliveryForecastProcessCompletionsOverride,
          },
          ResponsePeriod: {
            Value: formData.deliveryForecastResponsePeriodValue,
            OverrideDefaultValue:
              formData.deliveryForecastResponsePeriodOverride,
          },
        },
        EmailLanguage: {
          Value: formData.emailLanguageValue,
          OverrideDefaultValue: formData.emailLanguageOverride,
        },
        OrderProcessSettings: {
          DeliveryDateDeviation: {
            Value: formData.orderDeliveryDateDeviationValue,
            OverrideDefaultValue: formData.orderDeliveryDateDeviationOverride,
          },
          IsProcessActive: {
            Value: formData.orderIsProcessActiveValue,
            OverrideDefaultValue: formData.orderIsProcessActiveOverride,
          },
          ProcessCompletions: {
            Value: formData.orderProcessCompletionsValue,
            OverrideDefaultValue: formData.orderProcessCompletionsOverride,
          },
          ResponsePeriod: {
            Value: formData.orderResponsePeriodValue,
            OverrideDefaultValue: formData.orderResponsePeriodOverride,
          },
          InvoiceFormat: {
            Value: formData.orderInvoiceFormatValue,
            OverrideDefaultValue: formData.orderInvoiceFormatOverride,
          },
          AllowPreliminaryConfirmation: {
            Value: formData.orderAllowPreliminaryConfirmationValue,
            OverrideDefaultValue:
              formData.orderAllowPreliminaryConfirmationOverride,
          },
        },
        EInvoiceLanguage: {
          Value: formData.eInvoiceLanguageValue,
          OverrideDefaultValue: formData.eInvoiceLanguageOverride,
        },
        EInvoiceProcessSettings: {
          EInvoicePartnerType: {
            Value: formData.eInvoicePartnerTypeValue,
            OverrideDefaultValue: formData.eInvoicePartnerTypeOverride,
          },
          IsProcessActive: {
            Value: formData.eInvoiceIsProcessActiveValue,
            OverrideDefaultValue: formData.eInvoiceIsProcessActiveOverride,
          },
          IsEInvoiceSupplierEnabled: {
            Value: formData.isEInvoiceSupplierEnabledValue,
            OverrideDefaultValue: formData.isEInvoiceSupplierEnabledOverride,
          },
          EInvoiceAttachmentDocumentType: {
            Value: formData.eInvoiceAttachmentDocumentTypeValue,
            OverrideDefaultValue:
              formData.eInvoiceAttachmentDocumentTypeOverride,
          },
        },
      },
    }
  }

  async function handleSubmit(formData: EditSupplierFormDto): Promise<void> {
    try {
      setIsRequestRunning(true)

      await requestUpdateSupplier(id, formValuesToSupplierDetails(formData))

      mutate()

      showToast({
        closable: true,
        severity: 'success',
        summary: t('supplier.title.edit.save.messages.success.summary'),
        detail: t('supplier.title.edit.save.messages.success.detail', {
          supplier: formData.name,
        }),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t('supplier.title.edit.save.messages.failure.summary'),
        detail: errorMessage,
      })

      // The error is thrown to formik
      throw error
    } finally {
      setIsRequestRunning(false)
    }
  }

  async function handleActivate(): Promise<void> {
    try {
      setIsRequestRunning(true)

      await requestActivateSupplier(supplier?.Id as string)

      showToast({
        closable: true,
        severity: 'success',
        summary: t('supplier.title.activate.messages.success.summary'),
        detail: t('supplier.title.activate.messages.success.detail', {
          supplier: supplier?.Name,
        }),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t('supplier.title.activate.messages.failure.summary'),
        detail: errorMessage,
      })
    } finally {
      globalMutate((key: string) =>
        key.toLowerCase().startsWith('/selfservices/suppliers'),
      )
      globalMutate((key: string) =>
        key.toLowerCase().startsWith(`/tenants/${tenantId}/projects`),
      )

      setIsRequestRunning(false)
    }
  }

  async function handleDeactivate(): Promise<void> {
    try {
      setIsRequestRunning(true)

      await requestDeactivateSupplier(supplier?.Id as string)

      showToast({
        closable: true,
        severity: 'success',
        summary: t(
          'supplier.title.deactivate.dialog.deactivate.messages.success.summary',
        ),
        detail: t(
          'supplier.title.deactivate.dialog.deactivate.messages.success.detail',
          {
            supplier: supplier?.Name,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      showToast({
        closable: true,
        severity: 'error',
        sticky: true,
        summary: t(
          'supplier.title.deactivate.dialog.deactivate.messages.failure.summary',
        ),
        detail: errorMessage,
      })
    } finally {
      globalMutate((key: string) =>
        key.toLowerCase().startsWith('/selfservices/suppliers'),
      )
      globalMutate((key: string) =>
        key.toLowerCase().startsWith(`/tenants/${tenantId}/projects`),
      )

      setIsRequestRunning(false)
    }
  }

  return (
    <SupplierForm
      isLoading={isLoading}
      isRequestRunning={isRequestRunning}
      supplier={supplier}
      formValues={supplierDetailToFormValues(supplier)}
      submit={handleSubmit}
      activateSupplier={handleActivate}
      deactivateSupplier={handleDeactivate}
    />
  )
}
